type UserData = {
    userId: string;
    company: string;
    countryCode: string;
    department: string;
    jobRole: string;
    region: string;
    isExternal: boolean;
};

type WorkspaceData = {
    market?: string;
    industry?: string;
    brand?: string;
    client?: string;
    region?: string;
    custom?: string;
};

type ApplicationData = {
    applicationId: string;
    applicationInstanceId: string;
    productType: string;
    nocodeApptype?: string;
    productName: string;
    productCategory: string;
    productSubcategory: string;
};

type ContextData = {
    contextType: "os" | "standalone";
    project?: string;
    tenantName?: string;
};

type WindowWithDataLayer = Record<string, unknown> & Window;

const hashEmail = async (email: string) => {
    const normalizedEmail = email.trim().toLowerCase();
    const msgBuffer = new TextEncoder().encode(normalizedEmail);
    const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);
    return Array.from(new Uint8Array(hashBuffer))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
};

export class GA4Tracker {
    private dataLayerName: string;
    private userData: UserData | null = null;
    private workspaceData: WorkspaceData = {};
    private applicationData: ApplicationData | null = null;
    private contextData: ContextData | null = null;
    private preventTracking: boolean;

    constructor(dataLayerName = "wppopen_dataLayer", preventTracking = false) {
        this.dataLayerName = dataLayerName;
        this.preventTracking = preventTracking;

        const windowWithDataLayer = window as unknown as WindowWithDataLayer;
        windowWithDataLayer[this.dataLayerName] = windowWithDataLayer[this.dataLayerName] ?? [];
    }

    private push(eventData: Record<string, unknown>) {
        if (this.preventTracking) {
            console.log("[GA4] Tracking prevented - running in Open OS iframe");
            return;
        }

        const windowWithDataLayer = window as unknown as WindowWithDataLayer;
        const dataLayer = windowWithDataLayer[this.dataLayerName] as unknown[];

        if (Array.isArray(dataLayer)) {
            dataLayer.push(eventData);
        } else {
            console.error("Data layer is not an array:", this.dataLayerName);
        }
    }

    async setUserData(userData: Omit<UserData, "userId"> & { email: string }) {
        const userId = await hashEmail(userData.email);
        this.userData = {
            userId,
            company: userData.company,
            countryCode: userData.countryCode,
            department: userData.department,
            jobRole: userData.jobRole,
            region: userData.region,
            isExternal: userData.isExternal,
        };
    }

    setWorkspaceData(workspaceData: WorkspaceData) {
        this.workspaceData = workspaceData;
    }

    setApplicationData(applicationData: ApplicationData) {
        this.applicationData = applicationData;
    }

    setContextData(contextData: ContextData) {
        this.contextData = contextData;
    }

    private getCommonProperties() {
        return {
            user_id: this.userData?.userId ?? "unknown",
            user_company: this.userData?.company ?? "unknown",
            user_country_code: this.userData?.countryCode ?? "unknown",
            user_department: this.userData?.department ?? "unknown",
            user_job_role: this.userData?.jobRole ?? "unknown",
            user_region: this.userData?.region ?? "unknown",
            user_is_external: this.userData?.isExternal ?? false,
            workspace_market: this.workspaceData.market ?? undefined,
            workspace_industry: this.workspaceData.industry ?? undefined,
            workspace_brand: this.workspaceData.brand ?? undefined,
            workspace_client: this.workspaceData.client ?? undefined,
            workspace_region: this.workspaceData.region ?? undefined,
            workspace_custom: this.workspaceData.custom ?? undefined,
            context_type: this.contextData?.contextType ?? "standalone",
            project: this.contextData?.project ?? undefined,
            tenant_name: this.contextData?.tenantName ?? undefined,
            ...(this.applicationData
                ? {
                      application_id: this.applicationData.applicationId,
                      application_instance_id: this.applicationData.applicationInstanceId,
                      product_type: this.applicationData.productType,
                      nocode_apptype: this.applicationData.nocodeApptype ?? undefined,
                      product_name: this.applicationData.productName,
                      product_category: this.applicationData.productCategory,
                      product_subcategory: this.applicationData.productSubcategory,
                  }
                : {}),
        };
    }

    trackUserLogin() {
        if (!this.userData) {
            console.error("User data must be set before tracking user login");
            return;
        }

        this.push({
            event: "user_login",
            ...this.getCommonProperties(),
        });
    }

    trackAppLaunch() {
        if (!this.applicationData) {
            console.error("Application data must be set before tracking app launch");
            return;
        }

        this.push({
            event: "app_launch",
            ...this.getCommonProperties(),
        });
    }

    trackPageView() {
        this.push({
            event: "page_view",
            ...this.getCommonProperties(),
        });
    }

    trackVirtualPageView(pageUrl: string, pageTitle: string, previousUrl: string) {
        this.push({
            event: "virtual_page_view",
            ...this.getCommonProperties(),
            pageUrl,
            pageTitle,
            previousUrl,
        });
    }

    trackClick(
        clickObject: "Image" | "Button" | "Link" | "Icon",
        clickText: string,
        clickPagePath: string = window.location.pathname,
        clickTargetPath?: string,
    ) {
        this.push({
            event: "click",
            ...this.getCommonProperties(),
            click_object: clickObject,
            click_text: clickText,
            click_page_path: clickPagePath,
            click_target_path: clickTargetPath ?? undefined,
        });
    }

    trackFeatureActivate(
        featureCategory: string,
        featureContext: string,
        freeformValues: Record<number, string | number> = {},
    ) {
        const eventData: Record<string, unknown> = {
            event: "feature_activate",
            ...this.getCommonProperties(),
            feature_category: featureCategory,
            feature_context: featureContext,
        };

        Object.entries(freeformValues).forEach(([key, value]) => {
            const index = parseInt(key);
            if (!isNaN(index) && index >= 1 && index <= 10) {
                eventData[`feature_freeform_${index}`] = value;
            }
        });

        this.push(eventData);
    }
}
